import type { CompactKind, CompactMeta, CompactVertical } from "./types";

/**
 * Alla compact keyword-sidor. Lägg till en rad här och motsvarande innehåll i
 * `content/<fil>.ts` – testet i `__tests__/compactPages.test.ts` kontrollerar
 * att båda finns, att slugen inte krockar med kommuner eller befintliga
 * rutter och att metadatan håller längdgränserna.
 */

type Row = Omit<CompactMeta, "vertical" | "kind">;

const bransch = (rows: Row[]): CompactMeta[] =>
  rows.map((r) => ({ ...r, vertical: "elavtal", kind: "bransch" }));

const situation = (vertical: CompactVertical, rows: Row[]): CompactMeta[] =>
  rows.map((r) => ({ ...r, vertical, kind: "situation" }));

/** Titelns utrymme före " | Elmarknad.se" (MAX_TITLE_LENGTH 60 i seoTitles). */
export const TITLE_BUDGET = 45;

const b = (slug: string, navLabel: string, plural: string, group: string, focus: string): Row => ({
  slug,
  navLabel,
  group,
  keyword: `elavtal för ${plural}`,
  h1: `Elavtal för ${plural}`,
  title:
    [`Elavtal för ${plural} – jämför och få offert`, `Elavtal för ${plural} – begär offert`].find(
      (t) => t.length <= TITLE_BUDGET,
    ) ?? `Elavtal för ${plural}`,
  description: `Elavtal för ${plural}: ${focus} Jämför avtalsformer och begär offert för verksamhetens förbrukning.`,
});

export const ELAVTAL_BRANSCHER: CompactMeta[] = bransch([
  b("restauranger", "Restauranger", "restauranger", "Hotell & restaurang", "kyla, kök och ventilation som går hela dagen."),
  b("hotell", "Hotell", "hotell", "Hotell & restaurang", "dygnet-runt-drift med värme, varmvatten och tvätt."),
  b("bagerier", "Bagerier", "bagerier", "Hotell & restaurang", "ugnar som drar mycket el tidigt på morgonen."),
  b("butiker", "Butiker & handel", "butiker", "Handel", "belysning, ventilation och öppettider som styr förbrukningen."),
  b("livsmedelsbutiker", "Livsmedelsbutiker", "livsmedelsbutiker", "Handel", "kyl och frys som står för merparten av elen."),
  b("bilhandlare", "Bilhandlare", "bilhandlare", "Handel", "stora hallar och laddning av elbilar."),
  b("fastighetsagare", "Fastighetsägare", "fastighetsägare", "Fastigheter", "fastighetsel för flera anläggningar och hyresgäster."),
  b("fastighetsforvaltare", "Fastighetsförvaltare", "fastighetsförvaltare", "Fastigheter", "många anläggningar som ska samlas och följas upp."),
  b("kontor", "Kontor & kontorshotell", "kontor", "Fastigheter", "förbrukning som följer arbetstid och ventilation."),
  b("lagerlokaler", "Lagerlokaler", "lagerlokaler", "Fastigheter", "stora ytor med belysning, portar och truckladdning."),
  b("industri", "Industriföretag", "industriföretag", "Industri & logistik", "hög förbrukning och möjlig nedsatt energiskatt."),
  b("logistikforetag", "Logistikföretag", "logistikföretag", "Industri & logistik", "terminaler, truckladdning och drift dygnet runt."),
  b("akerier", "Åkerier", "åkerier", "Industri & logistik", "verkstad, motorvärmare och laddning av ellastbilar."),
  b("kylanlaggningar", "Kyl- och fryslager", "kyl- och fryslager", "Industri & logistik", "kylmaskiner som går året runt."),
  b("serverhallar", "Serverhallar & datacenter", "serverhallar", "Industri & logistik", "jämn och mycket hög förbrukning dygnet runt."),
  b("jordbruk", "Jordbruk & lantbruk", "jordbruk", "Industri & logistik", "torkar, fläktar och mjölkning med säsongstoppar."),
  b("bilverkstader", "Bilverkstäder", "bilverkstäder", "Fordon & service", "kompressorer, lyftar och uppvärmda lokaler."),
  b("vardcentraler", "Vårdcentraler", "vårdcentraler", "Vård & omsorg", "dagtidsdrift med krav på stabil drift."),
  b("tandlakare", "Tandläkarkliniker", "tandläkare", "Vård & omsorg", "kompressorer, sterilisering och behandlingsutrustning."),
  b("aldreboenden", "Äldreboenden", "äldreboenden", "Vård & omsorg", "drift dygnet runt med kök, tvätt och värme."),
  b("sjukhus", "Sjukhus", "sjukhus", "Vård & omsorg", "mycket hög förbrukning och krav på driftsäkerhet."),
  b("skolor", "Skolor & förskolor", "skolor och förskolor", "Offentligt & föreningar", "förbrukning som följer läsår och skoltid."),
  b("kyrkor", "Kyrkor & församlingar", "kyrkor", "Offentligt & föreningar", "stora rum som värms upp ojämnt över veckan."),
  b("gym", "Gym & träningsanläggningar", "gym", "Fritid", "ventilation, duschar och långa öppettider."),
  b("spa-badhus", "Spa & badhus", "spa och badhus", "Fritid", "uppvärmning av vatten och luft året runt."),
  b("camping", "Campingplatser", "campingplatser", "Fritid", "säsongstoppar och el till gästernas platser."),
  b("golfklubbar", "Golfklubbar", "golfklubbar", "Fritid", "bevattning, klubbhus och laddning av golfbilar."),
]);

export const ELAVTAL_SITUATIONER: CompactMeta[] = situation("elavtal", [
  {
    slug: "elbil",
    navLabel: "Elbilsägare",
    group: "Utrustning hemma",
    keyword: "elavtal för elbilsägare",
    h1: "Elavtal för elbilsägare",
    title: "Elavtal för elbilsägare – ladda billigt",
    description: "Elavtal för elbilsägare: så väljer du avtal när bilen laddas hemma, varför timpris ofta lönar sig och hur mycket elbilen ökar förbrukningen.",
  },
  {
    slug: "varmepump",
    navLabel: "Värmepump",
    group: "Utrustning hemma",
    keyword: "elavtal för värmepump",
    h1: "Elavtal för värmepump",
    title: "Elavtal för värmepump – välj rätt avtalsform",
    description: "Elavtal för hus med värmepump: förbrukningen är störst på vintern. Se när fast pris, timpris eller vintersäkrat avtal passar bäst.",
  },
  {
    slug: "solceller",
    navLabel: "Solcellsägare",
    group: "Utrustning hemma",
    keyword: "elavtal för solcellsägare",
    h1: "Elavtal för solcellsägare",
    title: "Elavtal för solcellsägare – köp och sälj",
    description: "Elavtal för dig med solceller: varför timpris passar, vad som gäller när du säljer överskottsel och vad du ska jämföra mellan elbolagen.",
  },
  {
    slug: "elvarme",
    navLabel: "Elvärme",
    group: "Utrustning hemma",
    keyword: "elavtal för eluppvärmt hus",
    h1: "Elavtal för eluppvärmt hus",
    title: "Elavtal för eluppvärmt hus – välj rätt",
    description: "Elavtal för hus med direktverkande el eller elpanna: hög förbrukning på vintern gör avtalsvalet viktigt. Så jämför du fast, rörligt och vintersäkrat.",
  },
  {
    slug: "utan-bindningstid",
    navLabel: "Utan bindningstid",
    group: "Avtalsvillkor",
    keyword: "elavtal utan bindningstid",
    h1: "Elavtal utan bindningstid",
    title: "Elavtal utan bindningstid – byt när du vill",
    description: "Elavtal utan bindningstid: vilka avtalsformer som saknar bindning, vad uppsägningstiden är och vad du ska kontrollera innan du tecknar.",
  },
  {
    slug: "utan-manadsavgift",
    navLabel: "Utan månadsavgift",
    group: "Avtalsvillkor",
    keyword: "elavtal utan månadsavgift",
    h1: "Elavtal utan månadsavgift",
    title: "Elavtal utan månadsavgift – jämför priser",
    description: "Elavtal utan månadsavgift: när det lönar sig, var avgiften i stället brukar ligga och hur du räknar fram totalkostnaden för din förbrukning.",
  },
  {
    slug: "gron-el",
    navLabel: "Grön el",
    group: "Avtalsvillkor",
    keyword: "elavtal med grön el",
    h1: "Elavtal med grön el",
    title: "Elavtal med grön el – så fungerar det",
    description: "Elavtal med grön el: vad ursprungsgarantier innebär, skillnaden mellan förnybar el och Bra Miljöval och vad det brukar kosta extra.",
  },
  {
    slug: "fast-1-ar",
    navLabel: "Fast pris 1 år",
    group: "Avtalsvillkor",
    keyword: "fast elpris 1 år",
    h1: "Fast elpris 1 år",
    title: "Fast elpris 1 år – när lönar det sig?",
    description: "Fast elpris i ett år: så fungerar avtalet, när det brukar löna sig att binda, vad brytavgiften kan bli och hur du jämför erbjudandena.",
  },
  {
    slug: "fast-3-ar",
    navLabel: "Fast pris 3 år",
    group: "Avtalsvillkor",
    keyword: "fast elpris 3 år",
    h1: "Fast elpris 3 år",
    title: "Fast elpris 3 år – lönar det sig?",
    description: "Fast elpris i tre år: för vem längre bindning passar, hur priset sätts, vad som händer om du flyttar och vad du ska jämföra innan du binder.",
  },
  {
    slug: "smaforetag",
    navLabel: "Småföretag",
    group: "Företag",
    keyword: "elavtal för småföretag",
    h1: "Elavtal för småföretag",
    title: "Elavtal för småföretag – begär offert",
    description: "Elavtal för småföretag: vad som skiljer företagsavtal från privatavtal, vilken avtalsform som passar och hur du får offerter för din förbrukning.",
  },
  {
    slug: "enskild-firma",
    navLabel: "Enskild firma",
    group: "Företag",
    keyword: "elavtal för enskild firma",
    h1: "Elavtal för enskild firma",
    title: "Elavtal för enskild firma – så väljer du",
    description: "Elavtal för enskild firma: när du behöver ett företagsavtal, hur moms och avdrag fungerar och vad du ska tänka på om verksamheten finns i bostaden.",
  },
  {
    slug: "dodsbo",
    navLabel: "Dödsbo",
    group: "Livssituation",
    keyword: "elavtal för dödsbo",
    h1: "Elavtal för dödsbo",
    title: "Elavtal för dödsbo – så hanterar du elen",
    description: "Elavtal för dödsbo: vem som ansvarar för elen, hur du byter avtal eller säger upp det och hur du undviker dyra tillsvidarepriser under tiden.",
  },
]);

export const LADDBOX_SIDOR: CompactMeta[] = situation("laddboxar", [
  {
    slug: "brf",
    navLabel: "Laddbox för BRF",
    group: "Bostadstyp",
    keyword: "laddbox för brf",
    h1: "Laddbox för bostadsrättsförening",
    title: "Laddbox för BRF – planera och jämför offerter",
    description: "Laddbox för brf: så planerar föreningen laddplatser, vad lastbalansering och debitering innebär och hur ni får offerter från flera installatörer.",
  },
  {
    slug: "villa",
    navLabel: "Laddbox för villa",
    group: "Bostadstyp",
    keyword: "laddbox för villa",
    h1: "Laddbox för villa",
    title: "Laddbox för villa – välj rätt och jämför",
    description: "Laddbox för villa: vilken effekt du behöver, vad huvudsäkringen betyder, hur skattereduktionen fungerar och hur du jämför offerter.",
  },
  {
    slug: "foretag",
    navLabel: "Laddbox för företag",
    group: "Bostadstyp",
    keyword: "laddbox för företag",
    h1: "Laddbox för företag",
    title: "Laddbox för företag – jämför offerter",
    description: "Laddbox för företag: laddplatser för personal, kunder och tjänstebilar. Så dimensionerar ni, hanterar debitering och jämför offerter.",
  },
  {
    slug: "tesla",
    navLabel: "Laddbox till Tesla",
    group: "Bil",
    keyword: "laddbox till tesla",
    h1: "Laddbox till Tesla",
    title: "Laddbox till Tesla – vad du behöver veta",
    description: "Laddbox till Tesla: vilken laddeffekt bilen klarar, om du behöver Teslas egen laddare och hur du får offerter på installation hemma.",
  },
  {
    slug: "volvo",
    navLabel: "Laddbox till Volvo",
    group: "Bil",
    keyword: "laddbox till volvo",
    h1: "Laddbox till Volvo",
    title: "Laddbox till Volvo – välj rätt för din elbil",
    description: "Laddbox till Volvo: så väljer du laddbox till elbil eller laddhybrid från Volvo, vilken effekt som räcker och hur du jämför installatörer.",
  },
  {
    slug: "lastbalansering",
    navLabel: "Lastbalansering",
    group: "Funktion & pris",
    keyword: "laddbox med lastbalansering",
    h1: "Laddbox med lastbalansering",
    title: "Laddbox med lastbalansering – så fungerar det",
    description: "Laddbox med lastbalansering: hur funktionen skyddar huvudsäkringen, när den behövs och varför den ofta är billigare än att höja säkringen.",
  },
  {
    slug: "installation-pris",
    navLabel: "Pris för installation",
    group: "Funktion & pris",
    keyword: "laddbox installation pris",
    h1: "Vad kostar det att installera en laddbox?",
    title: "Laddbox installation pris – vad kostar det?",
    description: "Laddbox installation pris: vad som påverkar kostnaden, hur skattereduktionen för grön teknik dras av och hur du jämför offerter rätt.",
  },
]);

export const SOLCELL_SIDOR: CompactMeta[] = situation("solceller", [
  {
    slug: "platak",
    navLabel: "Plåttak",
    group: "Tak",
    keyword: "solceller på plåttak",
    h1: "Solceller på plåttak",
    title: "Solceller på plåttak – montage och offert",
    description: "Solceller på plåttak: hur montaget görs på bandtäckt och profilerad plåt, vad som påverkar priset och hur du jämför offerter från installatörer.",
  },
  {
    slug: "tegeltak",
    navLabel: "Tegeltak",
    group: "Tak",
    keyword: "solceller på tegeltak",
    h1: "Solceller på tegeltak",
    title: "Solceller på tegeltak – så görs det",
    description: "Solceller på tegeltak: hur fästen monteras under pannorna, vad det betyder för tätheten och priset och hur du jämför offerter.",
  },
  {
    slug: "platt-tak",
    navLabel: "Platt tak",
    group: "Tak",
    keyword: "solceller på platt tak",
    h1: "Solceller på platt tak",
    title: "Solceller på platt tak – lutning och vikt",
    description: "Solceller på platt tak: ställningar med lutning, ballast eller förankring, vad taket måste bära och hur du får offerter för installationen.",
  },
  {
    slug: "brf",
    navLabel: "Solceller för BRF",
    group: "Bostadstyp",
    keyword: "solceller för brf",
    h1: "Solceller för bostadsrättsförening",
    title: "Solceller för BRF – så räknar föreningen",
    description: "Solceller för brf: hur föreningen använder solelen i fastigheten, vilka beslut som krävs och hur ni jämför offerter från flera installatörer.",
  },
  {
    slug: "fritidshus",
    navLabel: "Solceller för fritidshus",
    group: "Bostadstyp",
    keyword: "solceller för fritidshus",
    h1: "Solceller för fritidshus",
    title: "Solceller för fritidshus – lönar det sig?",
    description: "Solceller för fritidshus: när huset används mest på sommaren matchar produktionen väl. Så räknar du på lönsamheten och jämför offerter.",
  },
  {
    slug: "lantbruk",
    navLabel: "Solceller för lantbruk",
    group: "Bostadstyp",
    keyword: "solceller för lantbruk",
    h1: "Solceller för lantbruk",
    title: "Solceller för lantbruk – jämför offerter",
    description: "Solceller för lantbruk: stora takytor och förbrukning dagtid ger goda förutsättningar. Så dimensionerar du anläggningen och jämför offerter.",
  },
  {
    slug: "med-batteri",
    navLabel: "Solceller med batteri",
    group: "Paket",
    keyword: "solceller med batteri",
    h1: "Solceller med batteri",
    title: "Solceller med batteri – när lönar det sig?",
    description: "Solceller med batteri: hur batteriet lagrar solelen, när det lönar sig jämfört med att sälja överskottet och hur du jämför paketofferter.",
  },
  {
    slug: "med-laddbox",
    navLabel: "Solceller och laddbox",
    group: "Paket",
    keyword: "solceller och laddbox",
    h1: "Solceller och laddbox",
    title: "Solceller och laddbox – ladda med solel",
    description: "Solceller och laddbox i samma installation: hur du laddar elbilen med egen solel, vad en solstyrd laddbox gör och hur du jämför offerter.",
  },
  {
    slug: "komplett-paket",
    navLabel: "Solceller, batteri och laddbox",
    group: "Paket",
    keyword: "solceller batteri och laddbox",
    h1: "Solceller, batteri och laddbox i ett paket",
    title: "Solceller, batteri och laddbox i ett paket",
    description: "Solceller, batteri och laddbox i ett paket: hur delarna samverkar, vad skattereduktionen ger och hur du jämför kompletta offerter.",
  },
]);

export const BATTERI_SIDOR: CompactMeta[] = situation("batterier", [
  {
    slug: "till-befintliga-solceller",
    navLabel: "Till befintliga solceller",
    group: "Användning",
    keyword: "hembatteri till befintliga solceller",
    h1: "Hembatteri till befintliga solceller",
    title: "Hembatteri till befintliga solceller",
    description: "Hembatteri till befintliga solceller: om växelriktaren fungerar, AC- eller DC-koppling och hur du jämför offerter för att komplettera anläggningen.",
  },
  {
    slug: "stodtjanster",
    navLabel: "Stödtjänster",
    group: "Användning",
    keyword: "hembatteri för stödtjänster",
    h1: "Hembatteri för stödtjänster",
    title: "Hembatteri för stödtjänster – så fungerar det",
    description: "Hembatteri för stödtjänster: hur batteriet hjälper elnätet via en aggregator, vad som påverkar intäkten och vad du ska jämföra.",
  },
  {
    slug: "effekttariff",
    navLabel: "Effekttariff",
    group: "Användning",
    keyword: "hembatteri för effekttariff",
    h1: "Hembatteri för effekttariff",
    title: "Hembatteri för effekttariff – kapa toppar",
    description: "Hembatteri för effekttariff: hur batteriet sänker dina effekttoppar, när det lönar sig och hur du jämför offerter från flera installatörer.",
  },
]);

export const VARMEPUMP_SIDOR: CompactMeta[] = situation("varmepumpar", [
  {
    slug: "luft-vatten-villa",
    navLabel: "Luft-vatten för villa",
    group: "Hustyp",
    keyword: "luft-vattenvärmepump för villa",
    h1: "Luft-vattenvärmepump för villa",
    title: "Luft-vattenvärmepump för villa – jämför",
    description: "Luft-vattenvärmepump för villa med vattenburen värme: hur den fungerar, när den passar och hur du jämför offerter från flera installatörer.",
  },
  {
    slug: "bergvarme-villa",
    navLabel: "Bergvärme för villa",
    group: "Hustyp",
    keyword: "bergvärme för villa",
    h1: "Bergvärme för villa",
    title: "Bergvärme för villa – borrning och kostnad",
    description: "Bergvärme för villa: vad borrhålet innebär, när investeringen lönar sig jämfört med luft-vatten och hur du jämför offerter.",
  },
  {
    slug: "fritidshus",
    navLabel: "Värmepump för fritidshus",
    group: "Hustyp",
    keyword: "värmepump för fritidshus",
    h1: "Värmepump för fritidshus",
    title: "Värmepump för fritidshus – frostfritt billigt",
    description: "Värmepump för fritidshus: hur en luft-luftvärmepump håller huset frostfritt, vilken modell som klarar kyla och hur du jämför offerter.",
  },
  {
    slug: "luft-luft-lagenhet",
    navLabel: "Luft-luft för lägenhet",
    group: "Hustyp",
    keyword: "luft-luftvärmepump för lägenhet",
    h1: "Luft-luftvärmepump i lägenhet",
    title: "Luft-luftvärmepump i lägenhet – tillstånd",
    description: "Luft-luftvärmepump i lägenhet: vilka tillstånd som krävs från föreningen, var utedelen kan sitta och hur du jämför offerter.",
  },
  {
    slug: "brf",
    navLabel: "Värmepump för BRF",
    group: "Hustyp",
    keyword: "värmepump för brf",
    h1: "Värmepump för bostadsrättsförening",
    title: "Värmepump för BRF – jämför offerter",
    description: "Värmepump för brf: frånluft, berg eller luft-vatten för flerbostadshus, hur föreningen fattar beslut och jämför offerter.",
  },
  {
    slug: "byta-oljepanna",
    navLabel: "Byta oljepanna",
    group: "Byte av värmekälla",
    keyword: "byta oljepanna mot värmepump",
    h1: "Byta oljepanna mot värmepump",
    title: "Byta oljepanna mot värmepump – så går det",
    description: "Byta oljepanna mot värmepump: vilka alternativ som passar, vad bytet innebär för tank och rör och hur du jämför offerter.",
  },
  {
    slug: "ersatta-direktel",
    navLabel: "Ersätta direktverkande el",
    group: "Byte av värmekälla",
    keyword: "ersätta direktverkande el med värmepump",
    h1: "Ersätta direktverkande el med värmepump",
    title: "Ersätta direktverkande el med värmepump",
    description: "Ersätta direktverkande el med värmepump: luft-luft eller konvertering till vattenburen värme, vad det sparar och hur du jämför offerter.",
  },
]);

export const COMPACT_PAGES: CompactMeta[] = [
  ...ELAVTAL_BRANSCHER,
  ...ELAVTAL_SITUATIONER,
  ...LADDBOX_SIDOR,
  ...SOLCELL_SIDOR,
  ...BATTERI_SIDOR,
  ...VARMEPUMP_SIDOR,
];

const byKey = new Map(COMPACT_PAGES.map((p) => [`${p.vertical}/${p.slug}`, p]));

export function compactMeta(vertical: string, slug: string | undefined): CompactMeta | undefined {
  return slug ? byKey.get(`${vertical}/${slug}`) : undefined;
}

export const isCompactSlug = (vertical: string, slug: string | undefined) => Boolean(compactMeta(vertical, slug));

export const compactPath = (p: Pick<CompactMeta, "vertical" | "slug">) => `/${p.vertical}/${p.slug}`;

export function compactPagesFor(vertical: CompactVertical, kind?: CompactKind): CompactMeta[] {
  return COMPACT_PAGES.filter((p) => p.vertical === vertical && (!kind || p.kind === kind));
}

export function compactMetaByPath(path: string): CompactMeta | undefined {
  const [, vertical, slug] = path.split("/");
  return vertical ? compactMeta(vertical, slug) : undefined;
}
